import React, { useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import uploadIcon from "../../../../asset/images/upload-file.svg";
import view from "../../../../asset/images/view-eyes.svg";
import download from "../../../../asset/images/download-brokrage.svg";
import rejectedIcon from "../../../../asset/images/crossIcon.svg";
import {
  formatDate,
  noDataFound,
  toSentenceCase,
} from "../../../../CommonMethhods/CommonMethods";

// Read-only view of a bulk (BLK) dealer mapping batch - used for
// Pending / Approved / Rejected service requests.
function BulkRequestDetails({ batchId, status, rowData, summary }) {
  const [search, setSearch] = useState("");

  // "partial" is the same state as "pending" for the maker, so it is shown as Pending.
  const getStatusClass = (value = "") => {
    switch (value.toLowerCase()) {
      case "pending":
      case "partial":
        return "status-pending";
      case "approved":
        return "status-approved";
      case "rejected":
        return "status-rejected";
      case "draft":
        return "status-draft";
      case "sendback":
        return "status-sendback";
      default:
        return "";
    }
  };

  const getStatusLabel = (value = "") =>
    value.toLowerCase() === "partial" ? "Pending" : toSentenceCase(value);

  // Derives the "Category" UI label shown for a request - not a real backend
  // field (the backend never sends reasonType), just a categorization of the
  // free-text "reason" field. The separate "Reason" label elsewhere on this
  // page shows the backend's "remarks" field, not this "reason" field - see
  // the dlr-info-label usages below.
  const getReasonTypeLabel = (data) => {
    const reasonType = data?.reasonType;
    if (reasonType) {
      switch (reasonType.toLowerCase()) {
        case "newmapping":
          return "New Mapping";
        case "updatemapping":
          return "Update existing mapping";
        case "others":
          return "Others";
        default:
          return toSentenceCase(reasonType);
      }
    }

    const reason = data?.reason || "";
    if (reason.toLowerCase() === "new mapping") return "New Mapping";
    if (reason.toLowerCase() === "update existing mapping")
      return "Update existing mapping";
    if (reason) return "Others";
    return "-";
  };

  const requests = rowData?.requests || [];

  // A maker only sees their own requests, so the logged in user is the fallback
  // for the employee details when the response doesn't carry them.
  const employeeName =
    rowData?.makerName ||
    summary?.makerName ||
    localStorage.getItem("userName") ||
    "-";

  const employeeId =
    rowData?.makerId ||
    summary?.makerId ||
    localStorage.getItem("userId") ||
    "-";

  const approvalDocLink = rowData?.approvalDocLink || "";

  // rowData (the batch detail response) now returns these directly - see
  // GetApplicationDetails on the backend. summary (the dashboard grid row)
  // stays as a fallback only for a batch whose detail response predates that
  // (e.g. served by an older deployment). This is the maker's own batch-level
  // input, not a per-row value, so the first parsed row is never used here -
  // a row's own "Reason for Change"/remarks (from the xlsx) can legitimately
  // differ from what the maker typed for the batch.
  const category = rowData?.reason || summary?.reason;
  const reasonText = rowData?.remarks || summary?.remarks || "-";

  const searchText = search.trim().toLowerCase();
  const filteredRequests = searchText
    ? requests.filter((row) =>
        [row?.ucc, row?.clientName, row?.newDealerCode, row?.oldDealerCode]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(searchText))
      )
    : requests;

  const rejectionRemarks = [
    ...new Set(
      requests
        .flatMap((row) =>
          Array.isArray(row?.rejectionReason)
            ? row.rejectionReason
            : [row?.rejectionReason]
        )
        .filter(Boolean)
    ),
  ].join(", ");

  const handleViewDoc = () => {
    if (!approvalDocLink) return;
    window.open(approvalDocLink, "_blank", "noopener,noreferrer");
  };

  const handleDownloadDoc = () => {
    if (!approvalDocLink) return;
    window.open(approvalDocLink, "_self", "noopener,noreferrer");
  };

  return (
    <>
      <div className="ckr-section">
        <div className="draft-header">
          <div className="status-content">
            <span className="ckr-section-title">{batchId}</span>
          </div>
        </div>

        <div className="dlr-request-info">
          <div className="dlr-info-item">
            <span className="dlr-info-label">Employee Name</span>
            <span className="dlr-info-value">{employeeName}</span>
          </div>

          <div className="dlr-info-item">
            <span className="dlr-info-label">Employee ID</span>
            <span className="dlr-info-value">{employeeId}</span>
          </div>

          <div className="dlr-info-item">
            <span className="dlr-info-label">Total Request</span>
            <span className="dlr-info-value">
              {summary?.count || requests.length || 0}
            </span>
          </div>

          <div className="dlr-info-item">
            <span className="dlr-info-label">Reason Type</span>
            <span className="dlr-info-value">
              {getReasonTypeLabel({ reason: category })}
            </span>
          </div>

          <div className="dlr-info-item">
            <span className="dlr-info-label">Reason</span>
            <span className="dlr-info-value">{reasonText}</span>
          </div>
        </div>

        {rowData?.approvalDocFilename && (
          <>
            <span className="dlr-sub-title">Attachment</span>

            <div className="rm-attachment dlr-attachment mb-20">
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip id="file-tooltips">
                    {rowData?.approvalDocFilename}
                  </Tooltip>
                }
              >
                <div className="attachment-name">
                  <img src={uploadIcon} alt="upload icon" />
                  <span className="file-name">
                    {rowData?.approvalDocFilename}
                  </span>
                </div>
              </OverlayTrigger>

              <div className="attachment-actions">
                <button type="button" onClick={handleViewDoc}>
                  <img src={view} alt="view" className="view-icon" />
                  <span className="btn-title">View</span>
                </button>

                <button type="button" onClick={handleDownloadDoc}>
                  <img src={download} alt="download" />
                  <span className="btn-title">Download</span>
                </button>
              </div>
            </div>
          </>
        )}

        <div className="ckr-section-header">
          <p className="ckr-section-title">
            <span className="status-content">
              Row Preview ({requests.length})
              {status ? (
                <span className={getStatusClass(status)}>
                  {getStatusLabel(status)}
                </span>
              ) : null}
            </span>
          </p>

          <div className="d-flex align-items-center header-filter">
            <div className="ckr-search-box focusable-filter">
              <i className="fa fa-search" />
              <input
                type="text"
                placeholder="Search Clients"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="ckr-table-wrapper cdm-table">
          {filteredRequests.length > 0 ? (
            <table className="ckr-table draft-maker">
              <thead>
                <tr>
                  <th>Request Date</th>
                  <th>Employee ID</th>
                  <th>UCC / Client Code</th>
                  <th>Dealer Code</th>
                  <th>Effective Date</th>
                  <th>Reason Type</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((row, index) => (
                  <tr key={row?.requestId || index}>
                    <td>{row?.reqDate ? formatDate(row.reqDate) : "-"}</td>
                    <td>{row?.makerId || employeeId}</td>
                    <td>
                      {row?.clientName
                        ? `${row.clientName}- ${row?.ucc || ""}`
                        : row?.ucc || "-"}
                    </td>
                    <td>{row?.newDealerCode || "-"}</td>
                    <td>{row?.startDate ? formatDate(row.startDate) : "-"}</td>
                    {/* A row's own reason / remarks can come back blank for a bulk
                        upload, so the batch-level values stand in for it. */}
                    {/* <td>{getReasonTypeLabel(row)}</td> */}
                    {/* <td>
                      {getReasonTypeLabel(
                        row?.reasonType || row?.reason ? row : { reason: category }
                      )}
                    </td> */}
                    {/* <td>{row?.remarks || reasonText}</td> */}
                    {/* The batch-level reason / remarks are what the maker entered
                        for the whole upload, so they come first; a row's own
                        value is only used when the batch has none. */}
                    <td>
                      {getReasonTypeLabel(category ? { reason: category } : row)}
                    </td>
                    <td>{reasonText !== "-" ? reasonText : row?.remarks || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="step-content report-error-message">
              {noDataFound()}
            </div>
          )}
        </div>
      </div>

      {status?.toLowerCase() === "rejected" && rejectionRemarks && (
        <div className="reject-remark-cont">
          <div>
            <img src={rejectedIcon} alt="rejected" />
          </div>
          <div className="wrap-content">
            <span className="bold-text">Checker Rejection Remarks</span>
            <span className="remarks-val">{rejectionRemarks}</span>
          </div>
        </div>
      )}
    </>
  );
}

export default BulkRequestDetails;
